/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Reference } from '@1po/1po-bff-fe-spec/generated/basket/response/model/Reference';
import { ReferenceKey } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceKey';
import { ReferenceDiscount } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceDiscount';
import { TFunction } from 'i18next';
import { ROUTER_HOME } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { TrashAltIcon } from 'assets/icons';
import { Dialog, useDisclosure } from 'components/Dialog';
import { calculatePriceAfterDiscount } from 'components/Discount/Discount';
import { BANNER_HEIGHT } from 'components/Page/Header/AdvertisementSpace/InformationBanner';
import { HEADER_HEIGHT } from 'components/Page/Header/TopHeader/TopHeader';
import {
  attachOtherNonApplicableReferenceToVehicleRequest,
  clearActionStatuses,
  clearOtherReferenceAttachStatus,
  computeTotalPrice,
  getActionStatus,
  getBasketExternalSection,
  getBasketOtherSection,
  getBasketVehicles,
  getFileReferencesUploadStatusErrorRows,
  getOtherReferenceAttachStatus,
  removeReferencesRequest,
  setFileReferencesUploadStatusErrorRows,
  setReferencesIsSelected,
  setTotalPrices,
} from 'domains/basket/Basket.store';
import {
  ExternalBasketSectionLocal,
  OtherReferenceAttachStatus,
  REF_ATTACH_NON_COMPATIBLE,
  ReferenceLocal,
  VehicleLocal,
} from 'domains/basket/Basket.types';
import { getLastSearchedVehicleKey, getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import {
  collectAllReferences,
  collectReferenceKeys,
  collectReferenceNumbers,
  countSelectedActiveReferences,
} from 'domains/order/Order.mapper';
import { getDiscounts, getLoadedPrices, getStocksMapV2, ReferencePriceType } from 'domains/references';
import { isRefNumberDeposit } from 'domains/references/DepositReference';
import { getCurrency } from 'domains/user';
import { CART_STEPS_HEIGHT, CartAndValidationStepProps } from 'pages/CartPage/CartPage';
import { CartAddReferenceResult } from 'pages/CartPage/CartStep/CartAddReference';
import CartHeader from 'pages/CartPage/CartStep/CartHeader';
import CartItemsOthers from 'pages/CartPage/CartStep/CartItems/CartItemsOthers';
import CartItemsVehicle from 'pages/CartPage/CartStep/CartItems/CartItemsVehicle';
import {
  CartNotificationResultWrapper,
  CartStepContentWrapper,
  CartStepHeaderWrapper,
} from 'pages/CartPage/CartStep/CartStep.styled';
import CheckoutCardSection, { useCheckoutCardSideBySide } from 'pages/CartPage/CartStep/CheckoutCardSection';
import { CheckoutCardDesktopWrapper } from 'pages/CartPage/CartStep/CheckoutCardSection/CheckoutCardSection.styled';
import { theme } from 'styles';
import {
  Box,
  Checkbox,
  Flex,
  MarginBox,
  StickyBox,
  StickyBoxCustom,
  Text,
  TrashAltButton,
  WarningAlert,
  WhiteButton,
} from 'UI';
import { FOUND, hasData, NO_DATA, useBreakpointSelectorFull } from 'utils';
import { scrollToTopSmooth } from 'utils/hooks/useResetScroll';
import CartItemsExternal from './CartItems/CartItemsExternal';
import ExpiredDiscountsNotification from './ExpiredDiscountsNotification';
import { FileReferencesRowsErrors } from './FileUpload';

export const CART_HEADER_HEIGHT = 120;

const getRemoveDescription = (t: TFunction, isVehicleBasket: boolean, isLastSelected: boolean) => {
  if (isLastSelected) {
    return t('cart.action.removalText1', 'Deleting this reference will delete your vehicle basket.');
  }
  if (isVehicleBasket) {
    return t('cart.action.removalText2', 'This action will delete your vehicle basket and all attached references.');
  }
  return t('cart.action.removalText3', 'This action will delete any selected reference or vehicle basket.');
};

interface CartStepProps {
  addedOtherReference: string | undefined;
  setAddedByReferenceBox: (referenceNumber: string | undefined) => void;
  markRemovedReferences: (numberOfReferences: number) => void;
  numberOfAttachedReferences?: number;
  revertAttachOtherReference: () => void;
}

export const calculateTotalPrice = (
  references: Reference[],
  discounts: Map<string, ReferenceDiscount>,
  loadedPrices: {
    prices: any;
    reference: string;
  }[],
) => {
  return references.reduce((a: number, b: Reference) => {
    const discount = discounts.get(b.referenceNumber);
    const price = loadedPrices.find((p) => p.reference === b.referenceNumber)?.prices;
    if (price) {
      return a + calculatePriceAfterDiscount(discount, price, false) * b.quantity;
    } else {
      return a;
    }
  }, 0);
};

export const getReferenceNumbers = (
  vehiclesReferences: VehicleLocal[],
  otherReferences: ReferenceLocal[],
  extBasketsReferences: ExternalBasketSectionLocal[],
): string[] =>
  vehiclesReferences
    .map((r) => r.references)
    .reduce((a: ReferenceLocal[], b) => {
      return a.concat(b);
    }, [])
    .map((r) => r.referenceNumber)
    .concat(otherReferences.map((r) => r.referenceNumber))
    .concat(
      extBasketsReferences
        .map((r) => r.references)
        .reduce((a: ReferenceLocal[], b) => {
          return a.concat(b);
        }, [])
        .map((r) => r.referenceNumber),
    );

const getSelectionSummary = (
  otherReferences: ReferenceLocal[],
  vehicles: VehicleLocal[],
  externalBaskets: ExternalBasketSectionLocal[],
  disabledRefs: string[],
) => {
  const isDisabledRef = (ref: string) => disabledRefs.includes(ref);
  const countDisabledRefs = (references: ReferenceLocal[]) =>
    references.reduce((count, reference) => count + (isDisabledRef(reference.referenceNumber) ? 1 : 0), 0);

  const initialSummary = {
    selectedReferencesCount: otherReferences.filter((reference) => reference.isSelected).length,
    selectedActiveReferencesCount: countSelectedActiveReferences(otherReferences, disabledRefs),
    selectedDisabledReferencesCount: otherReferences.filter(
      (reference) => reference.isSelected && disabledRefs.includes(reference.referenceNumber),
    ).length,
    disabledReferencesCount: countDisabledRefs(otherReferences),
    totalReferencesCount: otherReferences.length,
  };

  const vehicleSummary = vehicles.reduce((summary, vehicle) => {
    summary.selectedReferencesCount += vehicle.references.filter((reference) => reference.isSelected).length;
    summary.selectedActiveReferencesCount += countSelectedActiveReferences(vehicle.references, disabledRefs);
    summary.selectedDisabledReferencesCount += vehicle.references.filter(
      (reference) => reference.isSelected && disabledRefs.includes(reference.referenceNumber),
    ).length;
    summary.disabledReferencesCount += countDisabledRefs(vehicle.references);
    summary.totalReferencesCount += vehicle.references.length;
    return summary;
  }, initialSummary);

  return externalBaskets.reduce((summary, basket) => {
    summary.selectedReferencesCount += basket.references.filter((reference) => reference.isSelected).length;
    summary.selectedActiveReferencesCount += countSelectedActiveReferences(basket.references, disabledRefs);
    summary.selectedDisabledReferencesCount += basket.references.filter(
      (reference) => reference.isSelected && disabledRefs.includes(reference.referenceNumber),
    ).length;
    summary.disabledReferencesCount += countDisabledRefs(basket.references);
    summary.totalReferencesCount += basket.references.length;
    return summary;
  }, vehicleSummary);
};

const CartStep = ({
  next,
  addedOtherReference,
  setAddedByReferenceBox,
  markRemovedReferences,
  isBanners = false,
}: CartAndValidationStepProps & CartStepProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const cartMaxWidth =
    useBreakpointSelectorFull()({ xs: 1000, sm: 1000, md: 1000, lg: 1300, xl: 1600, xml: 1600, xxl: 1600 }) ?? 1000;
  const [otherReferenceExpanded, setOtherReferenceExpanded] = useState(true);
  const [otherReferenceHighlight, setOtherReferenceHighlight] = useState<ReferenceKey | undefined>(undefined);
  const lastVehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const [disabledRefs, setDisabledRefs] = useState<string[]>([]);
  const large = useCheckoutCardSideBySide();
  const vehicles = useSelector(getBasketVehicles);
  const otherSection = useSelector(getBasketOtherSection);
  const externalSection = useSelector(getBasketExternalSection);

  const currency = useSelector(getCurrency);
  const errorRows = useSelector(getFileReferencesUploadStatusErrorRows);
  const [highlightedReference, setHighlightedReference] = useState<ReferenceKey | undefined>(undefined);

  const otherRefsActions = useSelector((state: RootState) => getActionStatus(state, addedOtherReference));
  const otherRefAskNonCompatibleStatus = useSelector((state: RootState) =>
    getOtherReferenceAttachStatus(state, REF_ATTACH_NON_COMPATIBLE),
  );

  const selectedVehiclesReferencesRaw = vehicles?.map((vehicle) => ({
    ...vehicle,
    references: vehicle.references?.filter((reference) => reference.isSelected),
  }));
  const selectedExternalsReferencesRaw = externalSection?.map((ext) => ({
    ...ext,
    references: ext.references?.filter((reference) => reference.isSelected),
  }));
  const selectedOtherReferences = otherSection?.references.filter((reference) => reference.isSelected);
  const selectedExtBasketsReferencesRaw = externalSection?.map((exBasket) => ({
    ...exBasket,
    references: exBasket.references?.filter((reference) => reference.isSelected),
  }));
  const selectedVehiclesReferences = selectedVehiclesReferencesRaw?.filter(
    (vehicle) => vehicle.references?.length > 0,
  ) as VehicleLocal[];
  const selectedExternalsReferences = selectedExternalsReferencesRaw?.filter(
    (ext) => ext.references?.length > 0,
  ) as ExternalBasketSectionLocal[];
  const activeSelectedVehiclesReferencesRaw = vehicles?.map((vehicle) => ({
    ...vehicle,
    references: vehicle.references?.filter(
      (reference) => reference.isSelected && !disabledRefs.includes(reference.referenceNumber),
    ),
  }));
  const activeSelectedVehiclesReferences = activeSelectedVehiclesReferencesRaw?.filter(
    (vehicle) => vehicle && vehicle.references && vehicle.references.length > 0,
  ) as VehicleLocal[];
  const activeSelectedOtherReferences = otherSection?.references.filter(
    (reference) => reference.isSelected && !disabledRefs.includes(reference.referenceNumber),
  );
  const activeSelectedExtBasketsReferencesRaw = externalSection?.map((exBasket) => ({
    ...exBasket,
    references: exBasket.references?.filter(
      (reference) => reference.isSelected && !disabledRefs.includes(reference.referenceNumber),
    ),
  }));
  const activeSelectedExtBasketsReferences = activeSelectedExtBasketsReferencesRaw?.filter(
    (exBasket) => exBasket && exBasket.references && exBasket.references.length > 0,
  ) as ExternalBasketSectionLocal[];

  const selectedReferences = getReferenceNumbers(
    selectedVehiclesReferences,
    selectedOtherReferences,
    selectedExternalsReferences,
  );
  const activeSelectedReferences = getReferenceNumbers(
    activeSelectedVehiclesReferences,
    activeSelectedOtherReferences,
    activeSelectedExtBasketsReferences,
  );
  const selectedReferencesDiscounts = useSelector((state: RootState) => getDiscounts(state, selectedReferences));
  const selectedReferencesLoadedPrices = useSelector((state: RootState) => getLoadedPrices(state, selectedReferences));
  const selectedReferencesStocks = useSelector((state: RootState) =>
    getStocksMapV2(
      state,
      activeSelectedReferences.filter((r) => !isRefNumberDeposit(r)),
    ),
  );
  const remapReferencesWithDiscount = (
    references: ReferenceLocal[],
    discounts: Map<string, ReferenceDiscount>,
    loadedPrices: {
      prices: NO_DATA | ReferencePriceType;
      reference: string;
    }[],
  ) => {
    return references.map((ref) => {
      const discount = discounts.get(ref.referenceNumber);
      const price = loadedPrices.find((p) => p.reference === ref.referenceNumber);
      if (!discount || !hasData(price?.prices)) {
        return ref;
      }

      return {
        ...ref,
        price: {
          ...ref.price,
          priceVatExcluded: String(calculatePriceAfterDiscount(discount, price?.prices, false)),
          priceVatIncluded: String(calculatePriceAfterDiscount(discount, price?.prices, true)),
        },
      };
    });
  };

  const setDisableRefs = (ref: string) => {
    setDisabledRefs((prevState) => [...prevState, ref]);
  };

  const unsetDisableRef = (ref: string) => {
    setDisabledRefs((prevState) => prevState.filter((r) => r !== ref));
  };

  const itemsCount =
    vehicles?.reduce(
      (acc: number, vehicle: VehicleLocal) => (vehicle.references ? acc + vehicle.references.length : acc),
      0,
    ) + (otherSection.references.length || 0);

  useEffect(() => {
    setDisabledRefs([]);
  }, [itemsCount]);
  const discountedSelectedVehiclesReferences: VehicleLocal[] = selectedVehiclesReferences.map((r) => {
    const discountedReferences = remapReferencesWithDiscount(
      r.references,
      selectedReferencesDiscounts,
      selectedReferencesLoadedPrices,
    );
    return { ...r, references: discountedReferences };
  });
  const discountedSelectedExternalsReferences: ExternalBasketSectionLocal[] = selectedExternalsReferences.map((r) => {
    const discountedReferences = remapReferencesWithDiscount(
      r.references,
      selectedReferencesDiscounts,
      selectedReferencesLoadedPrices,
    );
    return { ...r, references: discountedReferences };
  });
  const discountedSelectedOtherReferences: ReferenceLocal[] = remapReferencesWithDiscount(
    selectedOtherReferences,
    selectedReferencesDiscounts,
    selectedReferencesLoadedPrices,
  );

  const selectedTotalPriceVatIncluded = computeTotalPrice(
    discountedSelectedVehiclesReferences,
    discountedSelectedExternalsReferences,
    discountedSelectedOtherReferences,
    true,
  );
  const selectedTotalPriceVatExcludedNoDiscount = computeTotalPrice(
    selectedVehiclesReferences,
    selectedExternalsReferences,
    selectedOtherReferences,
    false,
  );
  const selectedTotalPriceVatExcluded = computeTotalPrice(
    discountedSelectedVehiclesReferences,
    discountedSelectedExternalsReferences,
    discountedSelectedOtherReferences,
    false,
  );

  const totalDiscount = selectedTotalPriceVatExcludedNoDiscount - selectedTotalPriceVatExcluded;

  useEffect(() => {
    dispatch(
      setTotalPrices({
        vatIncluded: selectedTotalPriceVatIncluded,
        vatExcluded: selectedTotalPriceVatExcluded,
        totalDiscount,
      }),
    );
  }, [dispatch, selectedTotalPriceVatIncluded, selectedTotalPriceVatExcluded, totalDiscount]);

  const containsOnlyFromOneVehicleBasket = selectedVehiclesReferences.length === 1;
  const containsNoOtherReferences = !selectedOtherReferences || selectedOtherReferences.length === 0;
  const containsExactlyOneReference =
    selectedVehiclesReferences?.reduce(
      (acc, vehicle) => (vehicle.references ? acc + vehicle.references.length : acc),
      0,
    ) === 1;
  const selectedLastReferenceInVehicleBasket =
    containsOnlyFromOneVehicleBasket && containsNoOtherReferences && containsExactlyOneReference;

  const containsAnyVehicleBasket =
    vehicles.filter(
      (vehicle) =>
        vehicle &&
        vehicle.references &&
        vehicle.references.filter((r) => r.isSelected).length === vehicle.references.length,
    ).length > 0;

  const disclosure = useDisclosure();
  const { onOpen } = disclosure;
  const disclosureAttach = useDisclosure();
  const { onOpen: onOpenAttachConfirmation } = disclosureAttach;

  useEffect(() => {
    if (otherRefAskNonCompatibleStatus.length > 0) {
      onOpenAttachConfirmation();
    }
  }, [otherRefAskNonCompatibleStatus, onOpenAttachConfirmation]);

  const handleConfirm = () => {
    const vehiclesWithReferences = selectedVehiclesReferencesRaw?.map((veh) => {
      return {
        vehicleKey: veh.vehicleDetail.vehicleKey,
        references: collectReferenceKeys(veh.references),
      };
    });
    const otherReferences = collectReferenceKeys(selectedOtherReferences);

    dispatch(clearActionStatuses());
    dispatch(setFileReferencesUploadStatusErrorRows([]));
    dispatch(
      removeReferencesRequest({
        vehiclesWithReferences,
        otherReferences,
      }),
    );
    const numberOfExternalReferences =
      selectedExternalsReferencesRaw?.reduce((acc, externalBasket) => acc + externalBasket.references.length, 0) ?? 0;
    const numberOfVehicleReferences = vehiclesWithReferences.reduce((acc, curr) => acc + curr.references.length, 0);
    const numberOfReferences = otherReferences.length + numberOfVehicleReferences + numberOfExternalReferences;
    markRemovedReferences(numberOfReferences);
  };
  const handleConfirmAttach = () => {
    otherRefAskNonCompatibleStatus.forEach((status) => {
      dispatch(
        attachOtherNonApplicableReferenceToVehicleRequest({
          vehicleKey: status.vehicleKey,
          reference: status.reference,
        }),
      );
    });
  };

  const handleCancelAttach = (otherRefAskNonCompatibleStatus: OtherReferenceAttachStatus[]) => {
    otherRefAskNonCompatibleStatus.forEach((status) => {
      dispatch(clearOtherReferenceAttachStatus({ reference: status.reference }));
    });
  };

  const dialogDescription = [
    getRemoveDescription(
      t,
      containsOnlyFromOneVehicleBasket && containsNoOtherReferences,
      selectedLastReferenceInVehicleBasket,
    ),
    t('common.dialog.confirmationQuestion', 'Would you like to confirm?'),
  ];

  const handleRedirect = () => {
    scrollToTopSmooth();
    history.push(
      lastVehicleKey && vehicleDetail?.catalogSource
        ? `${getCatalogSourceUrl(vehicleDetail.catalogSource)}/${lastVehicleKey}/vehicle`
        : ROUTER_HOME,
    );
  };

  const onRemoveSelected = () => {
    if (containsAnyVehicleBasket) {
      onOpen();
    } else {
      const numberOfExternalReferences =
        selectedExternalsReferencesRaw?.reduce((acc, externalBasket) => acc + externalBasket.references.length, 0) ?? 0;
      const numberOfVehicleReferences =
        selectedVehiclesReferencesRaw?.reduce((acc, vehicle) => acc + vehicle.references.length, 0) ?? 0;
      markRemovedReferences(numberOfVehicleReferences + selectedOtherReferences.length + numberOfExternalReferences);

      dispatch(clearActionStatuses());
      dispatch(setFileReferencesUploadStatusErrorRows([]));

      dispatch(
        removeReferencesRequest({
          vehiclesWithReferences: selectedVehiclesReferencesRaw?.map((veh) => {
            return {
              vehicleKey: veh.vehicleDetail.vehicleKey,
              references: collectReferenceKeys(veh.references),
            };
          }),
          otherReferences: collectReferenceKeys(selectedOtherReferences),
          externalBasketsWithReferences: selectedExtBasketsReferencesRaw?.map((extB) => {
            return {
              externalBasketId: extB.externalBasketId,
              references: collectReferenceNumbers(extB.references),
            };
          }),
        }),
      );
    }
  };

  function setVehiclesReferences(isSelected: boolean) {
    vehicles?.forEach((v) => {
      const referenceIds = collectAllReferences(v.references);
      dispatch(
        setReferencesIsSelected({
          isSelected,
          referenceIds,
          vehicleKey: v.vehicleDetail.vehicleKey,
        }),
      );
    });
  }

  function setExternalsReferences(isSelected: boolean) {
    externalSection?.forEach((ex) => {
      const referenceIds = collectAllReferences(ex.references);
      dispatch(
        setReferencesIsSelected({
          isSelected,
          referenceIds,
          vehicleKey: undefined,
          externalBasketId: ex.externalBasketId,
        }),
      );
    });
  }

  function doSelectAll(isSelected: boolean) {
    setVehiclesReferences(isSelected);
    setExternalsReferences(isSelected);
    const referenceIds = collectAllReferences(otherSection?.references);
    dispatch(
      setReferencesIsSelected({
        isSelected,
        referenceIds,
        vehicleKey: undefined,
      }),
    );
  }

  function selectAll(isSelected: boolean) {
    doSelectAll(isSelected);
  }

  const {
    selectedReferencesCount,
    selectedActiveReferencesCount,
    selectedDisabledReferencesCount,
    disabledReferencesCount,
    totalReferencesCount,
  } = getSelectionSummary(otherSection.references, vehicles, externalSection, disabledRefs);

  const isTrashButtonVisible = selectedReferencesCount !== 0 || disabledReferencesCount !== 0;
  const isSelectAllChecked = selectedReferencesCount > 0 && selectedReferencesCount === totalReferencesCount;
  const allReferencesStocksFound = Array.from(selectedReferencesStocks.values()).every((refStock) => {
    return refStock?.searchStatus === FOUND;
  });
  const disableCheckout =
    selectedActiveReferencesCount === 0 || selectedDisabledReferencesCount > 0 || !allReferencesStocksFound;

  return (
    <Flex direction={'column'}>
      <Box width={'100%'} data-cy={'cart-header'}>
        <StickyBox
          offsetTop={HEADER_HEIGHT + CART_STEPS_HEIGHT + (isBanners ? BANNER_HEIGHT : 0)}
          background={theme.color.white}
          height={CART_HEADER_HEIGHT}
        >
          <Flex justify={'center'} background={theme.color.white}>
            <CartStepHeaderWrapper direction={'column'} maxWidth={cartMaxWidth + 30}>
              <MarginBox mx={15}>
                <CartHeader setAddedByReferenceBox={setAddedByReferenceBox} isCartEmpty={false} />
                <Flex direction={'row'} align={'center'} minHeight={30} maxHeight={30}>
                  <Checkbox
                    checked={isSelectAllChecked}
                    onChange={selectAll}
                    label={`${t('cart.action.select_all', 'Select all items')}`}
                    data-cy={'select-all-items-checkbox'}
                  />
                  {isTrashButtonVisible && (
                    <MarginBox ml={15}>
                      <TrashAltButton onClick={onRemoveSelected} round bgColor={'grey95'} />
                    </MarginBox>
                  )}
                </Flex>
              </MarginBox>
            </CartStepHeaderWrapper>
          </Flex>
        </StickyBox>
      </Box>
      <Flex direction={'column'} justify={'center'}>
        <Flex direction={'row'} justify={'center'}>
          <CartStepContentWrapper mx={15}>
            <Flex direction={'row'} justify={'center'} maxWidth={cartMaxWidth}>
              <Flex direction={'column'} align={'flex-start'}>
                <StickyBox
                  offsetTop={HEADER_HEIGHT + CART_STEPS_HEIGHT + CART_HEADER_HEIGHT + (isBanners ? BANNER_HEIGHT : 0)}
                  background={theme.color.white}
                  width={'100%'}
                >
                  <Flex direction={'column'} align={'flex-start'} minWidth={'100%'}>
                    {otherRefsActions && (
                      <Flex>
                        <CartAddReferenceResult
                          otherSection={otherSection}
                          addedOtherReference={addedOtherReference}
                          otherReferencesAction={otherRefsActions}
                          setExpanded={setOtherReferenceExpanded}
                          setHighlightedRef={setOtherReferenceHighlight}
                        />
                      </Flex>
                    )}
                    {disabledRefs.length > 0 && (
                      <CartNotificationResultWrapper data-cy={'availabilities-changed-notification'}>
                        <MarginBox ml={-5}>
                          <Box background={theme.color.white}>
                            <MarginBox ml={5}>
                              <WarningAlert
                                message={
                                  <Flex direction={'column'}>
                                    <Text type="base" bold>
                                      {t(
                                        'cart.action.changed_availabilities.title',
                                        'Availabilities have changed since they’ve been added to your basket',
                                      )}
                                    </Text>
                                    <Text type="base">
                                      {t(
                                        'cart.action.changed_availabilities.description',
                                        'Disabled references will not be part of your order.',
                                      )}
                                    </Text>
                                  </Flex>
                                }
                              />
                            </MarginBox>
                          </Box>
                        </MarginBox>
                      </CartNotificationResultWrapper>
                    )}
                    <Dialog
                      disclosure={disclosureAttach}
                      title={t('common.dialog.information', 'Information')}
                      description={
                        t(
                          'common.dialog.compatibilityText',
                          'This reference is not compatible with the chosen vehicle.\nWould you like to confirm?',
                        ) as string
                      }
                      status={'info'}
                      handleCancel={() => handleCancelAttach(otherRefAskNonCompatibleStatus)}
                      handleConfirm={handleConfirmAttach}
                    />
                    <CartNotificationResultWrapper>
                      <FileReferencesRowsErrors errorRows={errorRows} />
                    </CartNotificationResultWrapper>
                    <Box height={1} />
                  </Flex>
                </StickyBox>
                <Flex direction={'column'} align={'flex-start'} minWidth={'100%'}>
                  {disabledRefs.length > 0 && <MarginBox mt={15} />}
                  <ExpiredDiscountsNotification setHighlightedReference={setHighlightedReference} />
                  {vehicles?.map((basketVehicle) => (
                    <CartItemsVehicle
                      key={basketVehicle.vehicleDetail?.vehicleKey}
                      basketVehicle={basketVehicle}
                      currency={currency}
                      markRemovedReferences={markRemovedReferences}
                      disabledRefs={disabledRefs}
                      highlightedReference={highlightedReference}
                      setDisableRefs={setDisableRefs}
                      unsetDisableRef={unsetDisableRef}
                    />
                  ))}
                  {externalSection.map((externalBasket) => (
                    <CartItemsExternal
                      key={externalBasket.externalBasketId}
                      externalBasket={externalBasket}
                      markRemovedReferences={markRemovedReferences}
                      currency={currency}
                      disabledRefs={disabledRefs}
                      highlightedReference={highlightedReference}
                      setDisableRefs={setDisableRefs}
                      unsetDisableRef={unsetDisableRef}
                    />
                  ))}
                  {otherSection && otherSection.references.length > 0 && (
                    <CartItemsOthers
                      key={'otherRefs'}
                      otherSection={otherSection}
                      currency={currency}
                      addedOtherReference={addedOtherReference}
                      expanded={otherReferenceExpanded}
                      setExpanded={setOtherReferenceExpanded}
                      highlightedRef={otherReferenceHighlight}
                      setHighlightedRef={setOtherReferenceHighlight}
                      markRemovedReferences={markRemovedReferences}
                      disabledRefs={disabledRefs}
                      highlightedReference={highlightedReference}
                      setDisableRefs={setDisableRefs}
                      unsetDisableRef={unsetDisableRef}
                    />
                  )}
                  <Box width={'100%'}>
                    <Flex justify={'flex-end'}>
                      <WhiteButton onClick={handleRedirect} data-cy={'keep-on-shopping-button'}>
                        {t('cart.action.keep_shopping', 'Keep on shopping')}
                      </WhiteButton>
                    </Flex>
                  </Box>
                </Flex>
              </Flex>
              {large && (
                <CheckoutCardDesktopWrapper data-cy={'checkout-card-desktop-wrapper'}>
                  <StickyBoxCustom
                    offsetTop={HEADER_HEIGHT + CART_STEPS_HEIGHT + CART_HEADER_HEIGHT + (isBanners ? BANNER_HEIGHT : 0)}
                  >
                    <MarginBox ml={15}>
                      <CheckoutCardSection
                        totalPriceVatIncluded={selectedTotalPriceVatIncluded ?? 0}
                        totalPriceVatExcluded={selectedTotalPriceVatExcluded ?? 0}
                        vehicles={selectedVehiclesReferences ?? []}
                        externalBaskets={selectedExternalsReferences ?? []}
                        otherReferences={selectedOtherReferences}
                        totalVat={(selectedTotalPriceVatIncluded ?? 0) - (selectedTotalPriceVatExcluded ?? 0)}
                        currency={currency}
                        onCheckout={next}
                        loading={false}
                        disabledCheckout={disableCheckout}
                        totalDiscount={totalDiscount}
                      />
                    </MarginBox>
                  </StickyBoxCustom>
                </CheckoutCardDesktopWrapper>
              )}
            </Flex>
          </CartStepContentWrapper>
        </Flex>
        {!large && (
          <>
            <MarginBox mt={30} />
            <Flex direction={'row'}>
              <Flex />
              <CheckoutCardSection
                totalPriceVatIncluded={selectedTotalPriceVatIncluded ?? 0}
                totalPriceVatExcluded={selectedTotalPriceVatExcluded ?? 0}
                totalVat={(selectedTotalPriceVatIncluded ?? 0) - (selectedTotalPriceVatExcluded ?? 0)}
                vehicles={selectedVehiclesReferences ?? []}
                externalBaskets={selectedExternalsReferences ?? []}
                otherReferences={selectedOtherReferences}
                currency={currency}
                onCheckout={next}
                disabledCheckout={disableCheckout}
                totalDiscount={totalDiscount}
              />
              <Flex />
            </Flex>
            <MarginBox mt={30} />
          </>
        )}
      </Flex>
      <Box height={140} />
      <Dialog
        disclosure={disclosure}
        title={t('common.dialog.actionRequired', 'Action required')}
        description={dialogDescription}
        icon={TrashAltIcon}
        status={'info'}
        handleConfirm={handleConfirm}
      />
    </Flex>
  );
};

export default CartStep;
